import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHead,
  ResponsiveTableHeader,
  ResponsiveTableRow,
} from "@/components/ui/responsive-table";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";

interface Client {
  id: string;
  companyName: string;
  contactName: string;
  email: string;
  phoneNumber: string;
}

interface ClientPage {
  content: Client[];
  page: { totalPages: number; totalElements: number };
}

type SortField = keyof Omit<Client, "id">;
type SortDirection = "asc" | "desc";

const PAGE_SIZE = 20;
const SORT_FIELDS: SortField[] = [
  "companyName",
  "contactName",
  "email",
  "phoneNumber",
];

function readSort(value: string | null): {
  field: SortField;
  direction: SortDirection;
} {
  const [field, direction] = (value ?? "").split(",");
  if (!SORT_FIELDS.includes(field as SortField))
    return { field: "companyName", direction: "asc" };
  return {
    field: field as SortField,
    direction: direction === "desc" ? "desc" : "asc",
  };
}

export default function ManageClients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("q") ?? "";
  const page = Math.max(0, Number(searchParams.get("page")) || 0);
  const sort = readSort(searchParams.get("sort"));
  const { toast } = useToast();

  const [searchInput, setSearchInput] = useState(searchTerm);
  const debouncedSearch = useDebounce(searchInput, 500);

  const updateParams = (changes: Record<string, string | null>) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) =>
          value ? next.set(key, value) : next.delete(key),
        );
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (trimmed !== searchTerm)
      updateParams({ q: trimmed || null, page: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["clients", searchTerm, page, sort.field, sort.direction],
    queryFn: async () => {
      const response = await axiosInstance.get<ClientPage>("client", {
        params: {
          ...(searchTerm && { searchTerm }),
          page,
          size: PAGE_SIZE,
          sort: `${sort.field},${sort.direction}`,
        },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isError) {
      toast({
        title: "Error",
        description: "Failed to fetch clients",
        variant: "destructive",
      });
    }
  }, [isError, toast]);

  const handleSort = (field: SortField) => {
    const direction =
      sort.field === field && sort.direction === "asc" ? "desc" : "asc";
    updateParams({ sort: `${field},${direction}`, page: null });
  };

  const clearSearch = () => {
    setSearchInput("");
    updateParams({ q: null, page: null });
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sort.field !== field) return null;
    return (
      <span className="ml-1 text-xs text-muted-foreground">
        {sort.direction === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const SortableHead = ({
    field,
    children,
  }: {
    field: SortField;
    children: string;
  }) => (
    <ResponsiveTableHead
      className="cursor-pointer hover:bg-muted/30 transition-colors"
      onClick={() => handleSort(field)}
      aria-sort={
        sort.field === field
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      {children} <SortIcon field={field} />
    </ResponsiveTableHead>
  );

  const clients = data?.content ?? [];

  return (
    <div className="container mx-auto p-6">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-foreground">Manage Clients</h1>
        <Link to="/admin/clients/create">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </Link>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          aria-label="Search clients"
          placeholder="Search company, contact, email or phone"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="h-40 flex items-center justify-center">
          <div className="text-muted-foreground">Loading clients...</div>
        </div>
      ) : (
        <div className="rounded-md border">
          <ResponsiveTable>
            <ResponsiveTableHeader>
              <ResponsiveTableRow>
                <SortableHead field="companyName">Company Name</SortableHead>
                <SortableHead field="contactName">Contact Name</SortableHead>
                <SortableHead field="email">Email</SortableHead>
                <SortableHead field="phoneNumber">Phone Number</SortableHead>
                <ResponsiveTableHead className="text-right">
                  Actions
                </ResponsiveTableHead>
              </ResponsiveTableRow>
            </ResponsiveTableHeader>
            <ResponsiveTableBody>
              {clients.map((client) => (
                <ResponsiveTableRow
                  key={client.id}
                  mobileTitle={client.companyName}
                >
                  <ResponsiveTableCell
                    mobileLabel="Company"
                    mobilePriority="high"
                  >
                    {client.companyName}
                  </ResponsiveTableCell>
                  <ResponsiveTableCell
                    mobileLabel="Contact"
                    mobilePriority="high"
                  >
                    {client.contactName}
                  </ResponsiveTableCell>
                  <ResponsiveTableCell
                    mobileLabel="Email"
                    mobilePriority="medium"
                  >
                    {client.email}
                  </ResponsiveTableCell>
                  <ResponsiveTableCell
                    mobileLabel="Phone"
                    mobilePriority="medium"
                  >
                    {client.phoneNumber}
                  </ResponsiveTableCell>
                  <ResponsiveTableCell className="text-right">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Link
                        to={`/admin/clients/${client.id}/edit`}
                        className="w-full sm:w-auto"
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full sm:w-auto"
                        >
                          Edit
                        </Button>
                      </Link>
                      <Link
                        to={`/admin/clients/${client.id}/events`}
                        className="w-full sm:w-auto"
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full sm:w-auto"
                        >
                          View Events
                        </Button>
                      </Link>
                    </div>
                  </ResponsiveTableCell>
                </ResponsiveTableRow>
              ))}
              {clients.length === 0 && (
                <ResponsiveTableRow>
                  <ResponsiveTableCell
                    colSpan={5}
                    className="text-center py-6 text-muted-foreground"
                  >
                    {searchTerm ? (
                      <div className="flex flex-col items-center gap-2">
                        <span>No clients match "{searchTerm}"</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearSearch}
                        >
                          Clear search
                        </Button>
                      </div>
                    ) : (
                      "No clients found"
                    )}
                  </ResponsiveTableCell>
                </ResponsiveTableRow>
              )}
            </ResponsiveTableBody>
          </ResponsiveTable>
        </div>
      )}

      <DataTablePagination
        currentPage={page}
        totalPages={data?.page.totalPages ?? 0}
        onPageChange={(next) =>
          updateParams({ page: next > 0 ? String(next) : null })
        }
      />
    </div>
  );
}
